import { useReadContract } from 'wagmi'
import { BaseVibeAbi } from '../utils/BaseVibeAbi'
import { CONTRACT_ADDRESS } from '../utils/constants'
import { useState, useEffect, useMemo } from 'react'
import { readContract } from '@wagmi/core'
import { config } from '../main'

export interface DayStats {
  dateKey: number
  date: string // YYYY-MM-DD format for display
  positiveVotes: number
  negativeVotes: number
  totalVotes: number
  positivePercentage: number
  sentiment: 'Bullish' | 'Bearish' | 'Neutral'
}

/**
 * Hook to fetch historical voting data for the last N days from the contract
 */
export function useHistoricalVibes(days: number = 7) {
  // Get today's date key (days since Unix epoch)
  const getTodayKey = () => {
    return Math.floor(Date.now() / (1000 * 86400))
  }

  // Convert dateKey to readable date string
  const dateKeyToString = (dateKey: number): string => {
    const date = new Date(dateKey * 86400 * 1000)
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Generate array of date keys for the last N days
  const dateKeys = useMemo(() => {
    const today = getTodayKey()
    const keys: number[] = []
    for (let i = days - 1; i >= 0; i--) {
      keys.push(today - i)
    }
    return keys
  }, [days])

  const [historicalData, setHistoricalData] = useState<DayStats[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Use getTodayStats to trigger refetching (as a heartbeat)
  const { data: todayData } = useReadContract({
    address: CONTRACT_ADDRESS as `0x${string}`,
    abi: BaseVibeAbi,
    functionName: 'getTodayStats',
    query: {
      enabled: CONTRACT_ADDRESS !== '0x0000000000000000000000000000000000000000',
      refetchInterval: 30000, // Refetch every 30 seconds
    },
  })

  // Fetch historical data when dateKeys or todayData changes
  useEffect(() => {
    const fetchHistoricalData = async () => {
      if (CONTRACT_ADDRESS === '0x0000000000000000000000000000000000000000') {
        setIsLoading(false)
        return
      }

      setIsLoading(true)

      try {
        // Fetch data for each date key from the contract
        const fetchPromises = dateKeys.map(async (dateKey) => {
          const dateStr = dateKeyToString(dateKey)
          
          try {
            // Fetch stats for this specific date from the contract
            const result = await readContract(config, {
              address: CONTRACT_ADDRESS as `0x${string}`,
              abi: BaseVibeAbi,
              functionName: 'getStatsForDate',
              args: [BigInt(dateKey)],
            })
            
            const [positiveVotes, negativeVotes, totalVotes] = result as readonly [bigint, bigint, bigint]
            
            const posVotes = Number(positiveVotes)
            const negVotes = Number(negativeVotes)
            const total = Number(totalVotes)
            const posPercentage = total > 0 ? Math.round((posVotes / total) * 100) : 0
            
            let sentiment: 'Bullish' | 'Bearish' | 'Neutral' = 'Neutral'
            if (total > 0) {
              sentiment = posVotes > negVotes ? 'Bullish' : (negVotes > posVotes ? 'Bearish' : 'Neutral')
            }

            return {
              dateKey,
              date: dateStr,
              positiveVotes: posVotes,
              negativeVotes: negVotes,
              totalVotes: total,
              positivePercentage: posPercentage,
              sentiment,
            }
          } catch (error) {
            console.error(`Error fetching data for date ${dateStr}:`, error)
            // Return empty data on error
            return {
              dateKey,
              date: dateStr,
              positiveVotes: 0,
              negativeVotes: 0,
              totalVotes: 0,
              positivePercentage: 0,
              sentiment: 'Neutral' as const,
            }
          }
        })

        // Wait for all fetch promises to complete
        const data = await Promise.all(fetchPromises)

        setHistoricalData(data)
        setIsLoading(false)
      } catch (error) {
        console.error('Error fetching historical data:', error)
        setIsLoading(false)
      }
    }

    fetchHistoricalData()
  }, [dateKeys, todayData])

  return {
    historicalData,
    isLoading,
    dateKeys,
  }
}

